import { useIntersectionObserver } from "@react-hookz/web";
import { AnimatePresence, motion } from "motion/react";
import {
  Dispatch,
  forwardRef,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { animationCurves } from "@utils/animationCurves";
import { useGlobalContext } from "@utils/GlobalContext";
import cx from "classnames";

type MobileSubMenuProps = {
  children: ReactNode;
  title: ReactNode;
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  hidden?: boolean;
};

const MobileSubMenu = forwardRef<HTMLDivElement, MobileSubMenuProps>(
  ({ children, title, isExpanded, setIsExpanded, hidden }, forwardedRef) => {
    const { switchableLocaleSettings } = useGlobalContext();
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [showNav, setShowNav] = useState(true);

    const hasLocaleSwitcher = switchableLocaleSettings.length > 1;

    const navIntersection = useIntersectionObserver(
      forwardedRef as React.MutableRefObject<HTMLDivElement | null>
    );

    useEffect(() => {
      if (navIntersection) {
        const isIntersecting = navIntersection.isIntersecting;

        if (showNav !== isIntersecting) {
          setShowNav(isIntersecting);
        }

        // Auto-collapse expanded filter panel when scrolling out of the section with this menu
        if (!isIntersecting && isExpanded) {
          setIsExpanded(false);
        }
      }
    }, [navIntersection, isExpanded, setIsExpanded, showNav]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          isExpanded &&
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setIsExpanded(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isExpanded, setIsExpanded]);

    return (
      <AnimatePresence>
        {!hidden && showNav && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={animationCurves.default}
            className={cx(
              "fixed left-4 right-4 z-30",
              hasLocaleSwitcher ? "bottom-[78px]" : "bottom-4"
            )}
            onClick={(e: React.MouseEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
          >
            <div className="bg-neutral-grey/50 backdrop-blur-[25px] rounded-lg text-brand-cream overflow-hidden">
              {/* Animated expanding filter options */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.menu
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    {children}
                  </motion.menu>
                )}
              </AnimatePresence>

              <button
                className="p-5 cursor-pointer appearance-none w-full"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {title}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);

export default MobileSubMenu;
